/** @format */

import React, { useState, useEffect, useMemo } from "react"
import SchemaOrg from "../components/SchemaOrg"
import SEO from "../components/SEO"
import { Link } from "react-router-dom"
import "./HomePage.css"
import YandexMap from "../components/YandexMap"

// Генерация стабильного псевдослучайного числа по seed
function seededRandom(seed) {
    const x = Math.sin(seed * 9301 + 49297) * 49297
    return x - Math.floor(x)
}

function HomePage() {
    const [scrollY, setScrollY] = useState(0)

    useEffect(() => {
        const handleScroll = () => setScrollY(window.scrollY)
        window.addEventListener("scroll", handleScroll, { passive: true })
        return () => window.removeEventListener("scroll", handleScroll)
    }, [])

    // FIX: Позиции светлячков вычисляются ОДИН РАЗ и не меняются при re-render
    const fireflies = useMemo(() => {
        return Array.from({ length: 15 }).map((_, i) => ({
            left: `${8 + seededRandom(i * 4 + 1) * 84}%`,
            top: `${15 + seededRandom(i * 4 + 2) * 55}%`,
            animationDelay: `${seededRandom(i * 4 + 3) * 6}s`,
            animationDuration: `${4 + seededRandom(i * 4 + 4) * 5}s`,
        }))
    }, [])

    return (
        <main>
            <SchemaOrg />
            <SEO
                title="Аренда загородного дома посуточно в Новой Москве"
                description="Снять загородный дом «Лесной домик» посуточно. До 60 гостей, 6 спален, русская баня, зона барбекю. Для праздников, корпоративов, выходных с друзьями. Деревня Мешково, 30 мин от МКАД."
                canonical="/"
            />

            {/* ===== HERO с анимированным лесом ===== */}
            <section className="hero">
                {/* Светлячки — позиции стабильны, только CSS-пульсация */}
                <div className="hero__fireflies">
                    {fireflies.map((style, i) => (
                        <div key={i} className="hero__firefly" style={style} />
                    ))}
                </div>

                {/* Луна */}
                <div className="hero__moon" style={{ transform: `translateY(${scrollY * 0.15}px)` }} />

                {/* Слои леса (параллакс) */}
                <div
                    className="hero__trees hero__trees--back"
                    style={{ transform: `translateY(${scrollY * 0.08}px)` }}
                />
                <div
                    className="hero__trees hero__trees--mid"
                    style={{ transform: `translateY(${scrollY * 0.15}px)` }}
                />
                <div
                    className="hero__trees hero__trees--front"
                    style={{ transform: `translateY(${scrollY * 0.25}px)` }}
                />

                {/* Туман */}
                <div className="hero__mist" />

                {/* Контент */}
                <div
                    className="hero__content"
                    style={{ transform: `translateY(${scrollY * 0.4}px)`, opacity: 1 - scrollY / 600 }}>
                    <p className="hero__subtitle">Загородный дом для отдыха и мероприятий</p>
                    <h1 className="hero__title font-display">Лесной домик</h1>
                    <div className="hero__line" />
                    <p className="hero__desc">
                        Уютный дом в окружении соснового леса и пруда.
                        <br />
                        До 60 гостей, 6 спален, русская баня, зона барбекю.
                    </p>
                    <Link to="/booking" className="btn-primary hero__btn">
                        Забронировать
                    </Link>
                </div>

                {/* Стрелка вниз */}
                <div className="hero__scroll-hint">
                    <span>Листайте вниз</span>
                    <div className="hero__scroll-arrow" />
                </div>
            </section>

            {/* ===== О ДОМЕ (с фото) ===== */}
            <section className="section section--white">
                <div className="container">
                    <h2 className="section-title">Добро пожаловать</h2>
                    <p className="section-subtitle">Место, где время замедляется</p>

                    <div className="about-grid">
                        <div className="about-grid__images">
                            <img
                                src="/images/house-2.webp"
                                alt="Гостиная"
                                className="about-grid__img about-grid__img--main"
                                loading="lazy"
                            />
                            <img
                                src="/images/grill-3.webp"
                                alt="Терраса"
                                className="about-grid__img about-grid__img--secondary"
                                loading="lazy"
                            />
                        </div>
                        <div className="about-grid__text">
                            <h3 className="about-grid__heading font-display">Ваш загородный дом мечты</h3>
                            <p>
                                «Лесной домик» — загородный дом для аренды посуточно, идеальный для отдыха в
                                кругу семьи, друзей или проведения мероприятий: дней рождения, корпоративов,
                                свадеб и тимбилдингов.
                            </p>
                            <p>
                                Дом расположен в деревне Мешково, Новомосковский округ Москвы — всего 30 минут
                                от МКАД. Рядом лесопарк, пруд для купания и рыбалки. Магазины и аптеки в пешей
                                доступности.
                            </p>
                            <p>
                                На первом этаже — просторный зал с большим столом до 60 человек, оборудованная
                                кухня, телевизор, караоке и гитара. На втором — 6 спальных комнат, детская
                                комната, зона отдыха с настольным теннисом, футболом и хоккеем. Рядом —
                                отдельный домик с русской баней.
                            </p>
                            <Link to="/gallery" className="btn-outline" style={{ marginTop: 8 }}>
                                Смотреть галерею →
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* ===== ПРЕИМУЩЕСТВА ===== */}
            <section className="section">
                <div className="container">
                    <h2 className="section-title">Всё для идеального отдыха</h2>
                    <p className="section-subtitle">
                        Мы продумали каждую деталь, чтобы ваш отдых был незабываемым
                    </p>

                    <div className="features-grid">
                        {[
                            {
                                emoji: "👥",
                                title: "До 60 гостей",
                                desc: "Просторный дом для больших компаний и мероприятий любого масштаба",
                            },
                            {
                                emoji: "🛏️",
                                title: "6 спален",
                                desc: "Комфортные комнаты с качественными кроватями и свежим бельём",
                            },
                            {
                                emoji: "☀️",
                                title: "Зона отдыха",
                                desc: "Караоке, гитара, настольный теннис, настольный футбол и настольный хоккей",
                            },
                            {
                                emoji: "🌲",
                                title: "Природа",
                                desc: "Рядом с домом — лесопарк, ведущий в город Московский, и пруд, где можно купаться и рыбачить",
                            },
                            {
                                emoji: "🔥",
                                title: "Русская баня",
                                desc: "Рядом с основным домом находится отдельный домик с баней",
                            },
                        ].map((item, i) => (
                            <div key={i} className="feature-card">
                                <div className="feature-card__emoji">{item.emoji}</div>
                                <h3 className="feature-card__title">{item.title}</h3>
                                <p className="feature-card__desc">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== ФОТОЛЕНТА ===== */}
            <section className="photo-strip">
                <div className="photo-strip__track">
                    {[
                        {
                            url: "/images/badroom-1.webp",
                            alt: "Спальня",
                        },
                        {
                            url: "/images/porch-1.webp",
                            alt: "Балкон",
                        },
                        {
                            url: "/images/hallway-1.webp",
                            alt: "Лес",
                        },
                        {
                            url: "/images/house-1.webp",
                            alt: "Дом",
                        },
                        {
                            url: "/images/kitchen-1.webp",
                            alt: "Кухня",
                        },
                        {
                            url: "/images/living-room-3.webp",
                            alt: "Гостиная",
                        },
                        {
                            url: "/images/living-room-1.webp",
                            alt: "Гостиная",
                        },
                        {
                            url: "/images/living-room-2.webp",
                            alt: "Отдых",
                        },
                    ].map((photo, i) => (
                        <div key={i} className="photo-strip__item">
                            <img src={photo.url} alt={photo.alt} loading="lazy" />
                        </div>
                    ))}
                </div>
            </section>

            {/* ===== ЧТО ВАС ЖДЁТ ===== */}
            <section className="section section--white">
                <div className="container">
                    <h2 className="section-title">Что вас ждёт</h2>
                    <p className="section-subtitle">Каждый уголок дома создан для вашего комфорта</p>

                    <div className="highlights">
                        {[
                            {
                                img: "/images/living-room-4.webp",
                                title: "Просторная гостиная",
                                text: "Просторный зал на первом этаже с большим столом до 60 человек — идеальное место для праздников и мероприятий. Телевизор, караоке и гитара для весёлого времяпровождения. На втором этаже — отдельная комната отдыха с настольным теннисом, настольным футболом и настольным хоккеем.",
                            },
                            {
                                img: "/images/grill-1.webp",
                                title: "Русская баня",
                                text: "Рядом с основным домом расположен отдельный домик с баней. После бани — прогулка к ближайшему пруду, где можно искупаться, или отдых на свежем воздухе в окружении природы и тишины.",
                            },
                            {
                                img: "/images/grill-3.webp",
                                title: "Терраса и барбекю",
                                text: "Большая оборудованная кухня на первом этаже со всем необходимым для приготовления еды. На территории — зона барбекю на свежем воздухе. Весной планируется строительство бассейна, детской площадки и оборудованной зоны отдыха у костра.",
                            },
                        ].map((item, i) => (
                            <div key={i} className={`highlight ${i % 2 !== 0 ? "highlight--reverse" : ""}`}>
                                <div className="highlight__img-wrap">
                                    <img src={item.img} alt={item.title} loading="lazy" />
                                </div>
                                <div className="highlight__text">
                                    <h3 className="font-display">{item.title}</h3>
                                    <p>{item.text}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== ИНФОРМАЦИЯ ===== */}
            <section className="section">
                <div className="container">
                    <h2 className="section-title">Информация для гостей</h2>
                    <p className="section-subtitle">Всё, что нужно знать перед бронированием</p>
                    <div className="info-grid">
                        {[
                            { emoji: "🕐", label: "Заезд", value: "с 15:00" },
                            { emoji: "🕛", label: "Выезд", value: "до 12:00" },
                            { emoji: "🐾", label: "Правила", value: "Без животных" },
                            { emoji: "🚗", label: "Парковка", value: "Бесплатная" },
                            { emoji: "📶", label: "Wi-Fi", value: "Есть" },
                            { emoji: "❄️", label: "Отопление", value: "Круглый год" },
                        ].map((item, i) => (
                            <div key={i} className="info-card">
                                <div className="info-card__emoji">{item.emoji}</div>
                                <div className="info-card__label">{item.label}</div>
                                <div className="info-card__value font-display">{item.value}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== CTA ===== */}
            <section className="cta-section">
                <div className="cta-section__bg" />
                <div className="container cta-section__inner">
                    <h2 className="font-display cta-section__title">Готовы к незабываемому отдыху?</h2>
                    <p className="cta-section__desc">
                        Забронируйте «Лесной домик» уже сегодня и подарите себе и близким выходные в окружении
                        природы, тишины и комфорта.
                    </p>
                    <Link to="/booking" className="btn-primary hero__btn">
                        Забронировать сейчас
                    </Link>
                </div>
            </section>

            {/* ===== КОНТАКТЫ ===== */}
            <section className="section section--white">
                <div className="container" style={{ textAlign: "center" }}>
                    <h2 className="section-title">Контакты</h2>
                    <p className="section-subtitle">Свяжитесь с нами любым удобным способом</p>
                    <div className="contacts-grid">
                        <div className="contact-card">
                            <div className="contact-card__icon">📞</div>
                            <div className="contact-card__label">Телефоны</div>
                            <strong>
                                <a href="tel:+79153663735" style={{ color: "black", textDecoration: "none" }}>
                                    +7 915 366 37 35
                                </a>
                                <br />
                                <a href="tel:+79661136344" style={{ color: "black", textDecoration: "none" }}>
                                    +7 966 113 63 44
                                </a>
                            </strong>
                        </div>
                        <div className="contact-card">
                            <div className="contact-card__icon">✈️</div>
                            <div className="contact-card__label">Telegram</div>
                            <strong>
                                <a
                                    href="https://t.me/+79661136344"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="payment-cta__telegram">
                                    Написать
                                </a>
                            </strong>
                        </div>
                        <div className="contact-card">
                            <div className="contact-card__icon">📍</div>
                            <div className="contact-card__label">Адрес</div>
                            <strong>
                                деревня Мешково, 173, Филимонковский район, Новомосковский административный
                                округ, Москва
                            </strong>
                        </div>
                    </div>
                    <YandexMap />
                </div>
            </section>
        </main>
    )
}

export default HomePage
